import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash2, Save } from "lucide-react";

const FIELD_TYPES = [
  { type: "text", label: "Text" },
  { type: "email", label: "E-Mail" },
  { type: "tel", label: "Telefon" },
  { type: "textarea", label: "Mehrzeilig" },
  { type: "select", label: "Auswahl" },
  { type: "checkbox", label: "Checkbox" },
  { type: "file", label: "Datei-Upload" },
];

function newId() { return Math.random().toString(36).slice(2, 11); }

export default function FormBuilder() {
  const [forms, setForms] = useState([]);
  const [editing, setEditing] = useState(null);
  const [submissions, setSubmissions] = useState([]);

  const load = () => api.get("/cms/forms").then(({ data }) => setForms(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (editing.id) await api.put(`/cms/forms/${editing.id}`, editing);
    else await api.post("/cms/forms", editing);
    setEditing(null); load();
  };
  const remove = async (id) => { if (!window.confirm("Löschen?")) return; await api.delete(`/cms/forms/${id}`); load(); };

  const openSubmissions = async (f) => {
    const { data } = await api.get(`/cms/forms/${f.id}/submissions`);
    setSubmissions(data);
    setEditing({ ...f, _viewSubs: true });
  };

  if (editing && editing._viewSubs) {
    return (
      <div className="p-8 lg:p-12 mv-fade-in max-w-5xl">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="font-display text-3xl">Einsendungen: {editing.name}</h1>
          <button onClick={() => setEditing(null)} className="mv-btn-ghost">Zurück</button>
        </div>
        <div className="mv-card p-5">
          {submissions.length === 0 ? <p style={{ color: "var(--mv-text-muted)" }}>Keine Einsendungen.</p> :
            <div className="space-y-3">
              {submissions.map((s) => (
                <div key={s.id} className="p-3 border rounded-lg" style={{ borderColor: "var(--mv-border)" }}>
                  <p className="text-xs mb-2" style={{ color: "var(--mv-text-muted)" }}>{new Date(s.created_at).toLocaleString("de-DE")}</p>
                  {Object.entries(s.data || {}).map(([k, v]) => (
                    <p key={k} className="text-sm"><b>{k}:</b> {String(v)}</p>
                  ))}
                </div>
              ))}
            </div>}
        </div>
      </div>
    );
  }

  if (editing) {
    const fields = editing.fields || [];
    return (
      <div className="p-8 lg:p-12 mv-fade-in max-w-3xl">
        <div className="flex items-end justify-between mb-8">
          <h1 className="font-display text-4xl">{editing.id ? "Formular bearbeiten" : "Neues Formular"}</h1>
          <div className="flex gap-2">
            <button onClick={() => setEditing(null)} className="mv-btn-ghost">Abbrechen</button>
            <button data-testid="save-form-btn" onClick={save} className="mv-btn"><Save className="w-4 h-4" /> Speichern</button>
          </div>
        </div>
        <div className="mv-card p-6 space-y-4">
          <div><label className="block text-xs mb-1">Name</label><input className="mv-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
          <div><label className="block text-xs mb-1">Erfolgs-Nachricht</label><input className="mv-input" value={editing.success_message || ""} onChange={(e) => setEditing({ ...editing, success_message: e.target.value })} /></div>
          <div>
            <p className="mv-label mb-3">Felder</p>
            {fields.map((f, i) => (
              <div key={f.id} className="p-3 border rounded-lg mb-2" style={{ borderColor: "var(--mv-border)" }}>
                <div className="grid grid-cols-3 gap-2">
                  <select className="mv-input text-sm" value={f.type} onChange={(e) => { const a = [...fields]; a[i] = { ...f, type: e.target.value }; setEditing({ ...editing, fields: a }); }}>
                    {FIELD_TYPES.map((ft) => <option key={ft.type} value={ft.type}>{ft.label}</option>)}
                  </select>
                  <input className="mv-input text-sm" placeholder="Label" value={f.label} onChange={(e) => { const a = [...fields]; a[i] = { ...f, label: e.target.value }; setEditing({ ...editing, fields: a }); }} />
                  <input className="mv-input text-sm" placeholder="name" value={f.name} onChange={(e) => { const a = [...fields]; a[i] = { ...f, name: e.target.value }; setEditing({ ...editing, fields: a }); }} />
                </div>
                <div className="flex justify-between items-center mt-2">
                  <label className="text-xs flex items-center gap-1.5"><input type="checkbox" checked={!!f.required} onChange={(e) => { const a = [...fields]; a[i] = { ...f, required: e.target.checked }; setEditing({ ...editing, fields: a }); }} />Erforderlich</label>
                  <button onClick={() => setEditing({ ...editing, fields: fields.filter((_, j) => j !== i) })} className="text-xs text-red-500"><Trash2 className="w-3 h-3 inline" /> Entfernen</button>
                </div>
              </div>
            ))}
            <button data-testid="add-field-btn" onClick={() => setEditing({ ...editing, fields: [...fields, { id: newId(), type: "text", label: "Neues Feld", name: `field_${fields.length + 1}`, required: false }] })} className="mv-btn-ghost text-xs"><Plus className="w-3 h-3" /> Feld hinzufügen</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 lg:p-12 mv-fade-in">
      <div className="flex items-end justify-between mb-10">
        <div><p className="mv-label mb-2">Eingaben</p><h1 className="font-display text-4xl">Formulare</h1></div>
        <button data-testid="new-form-btn" onClick={() => setEditing({ name: "", fields: [], success_message: "Vielen Dank!" })} className="mv-btn"><Plus className="w-4 h-4" /> Neues Formular</button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {forms.map((f) => (
          <div key={f.id} className="mv-card p-5" data-testid={`form-card-${f.id}`}>
            <h3 className="font-display text-lg mb-1">{f.name}</h3>
            <p className="text-xs mb-3" style={{ color: "var(--mv-text-muted)" }}>{(f.fields || []).length} Felder · ID: {f.id.slice(0, 8)}</p>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setEditing(f)} className="mv-btn-ghost text-xs">Bearbeiten</button>
              <button onClick={() => openSubmissions(f)} className="mv-btn-ghost text-xs">Einsendungen</button>
              <button onClick={() => remove(f.id)} className="text-xs text-red-500 ml-auto">Löschen</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
